//tsx boilerplate
import React, { createContext, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useParams } from 'react-router-dom';
import { makeSafeRequest } from '../../common/makeSafeRequest';
import { Project, ProjectsGetResponse } from '../../types/responses';

interface Props {
	children: React.ReactNode;
}

type AppInfoContextType = {
	projects: Project[] | undefined;
	isSuperuser: boolean;
	currentPid: number;
	setPid: (pid: number) => void;
	token: string | undefined;
	setToken: (token: string) => void;
	envVariables: { [key: string]: any };
	setEnvVariables: (envVariables: { [key: string]: any }) => void;
};

export const AppInfoContext = createContext<AppInfoContextType>({} as AppInfoContextType);

const AppInfoContextProvider: React.FC<Props> = ({ children }) => {
	const { pid } = useParams();
	const [projects, setProjects] = useState<Project[]>();
	const [isSuperuser, setIsSuperuser] = useState<boolean>(false);
	const [token, setToken] = useState<string | undefined>(
		localStorage.getItem('access') || undefined
	);
	const [currentPid, setCurrentPid] = useState<number>(pid ? parseInt(pid) : 0);
	const [envVariables, setEnvVariables] = useState({});

	useEffect(() => {
		if (token) {
			makeSafeRequest(`frontend_configuration/get-env`, 'GET', {
				onSuccess: (data) => {
					setEnvVariables(data as any);
				},
				onErrorMessage: 'Could not fetch environment variables.',
			});

			makeSafeRequest<ProjectsGetResponse>('projects/', 'GET', {
				onSuccess: (data) => {
					setProjects(data.projects);
				},
				onError: () => {
					notifications.show({
						title: 'Error',
						message: 'An error occurred while fetching projects.',
						color: 'red',
						autoClose: 5000,
					});
				},
			});

			makeSafeRequest<{ is_superuser: boolean }>('auth/is-superuser/', 'GET', {
				onSuccess: (data) => {
					setIsSuperuser(data.is_superuser);
				},
				onError: () => {
					notifications.show({
						title: 'Error',
						message: 'An error occurred while fetching user information.',
						color: 'red',
						autoClose: 5000,
					});
				},
			});
		}
	}, [token]);

	useEffect(() => {
		if (pid) {
			setPid(parseInt(pid));
		}
	}, [pid]);

	const setPid = (pid: number) => {
		localStorage.setItem('pid', pid.toString());
		setCurrentPid(pid);
	};

	const defaultAppInfoContext = {
		projects,
		isSuperuser,
		currentPid,
		setPid,
		token,
		setToken,
		envVariables,
		setEnvVariables,
	};

	return (
		<AppInfoContext.Provider value={defaultAppInfoContext}>{children}</AppInfoContext.Provider>
	);
};
export default AppInfoContextProvider;
